import React, { useEffect, useState } from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { passwordRequirements } from '../../utils/userInputUtils';
import LabeledInput from '../../components/Common/LabeledInput';
import StyledButton from '../../components/Common/StyledButton';
import { decodedData } from '../../utils/auth';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { createPasswordRequest, getBrandConfigurations } from '../../redux/coach/coachSlice';
import { LOGIN_ROUTE } from '../../utils/routes';

interface UserInfo {
  email: string;
  firstName: string;
  principalCoach: string;
}

const CreateAccountForm = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const createPasswordLoading = useAppSelector((state) => state.coach.createPasswordLoading);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const brandTag = queryParams.get('brandTag');
  const navigate = useNavigate();

  useEffect(() => {
    if (brandTag) {
      dispatch(getBrandConfigurations({ brandTag, navigate }));
    }
  }, [brandTag, dispatch, navigate]);

  useEffect(() => {
    if (!token) {
      navigate(LOGIN_ROUTE);
      return;
    }

    try {
      const decoded = decodedData(token);
      const { email, firstName, principalCoach } = decoded;

      if (!(email && firstName && principalCoach)) {
        navigate(LOGIN_ROUTE);
        return;
      }

      setUserInfo({ email, firstName, principalCoach });
    } catch (error) {
      navigate(LOGIN_ROUTE);
    }
  }, [token, navigate]);

  const isFormValid =
    userInfo && passwordRequirements(password, confirmPassword).every((req) => req.isValid());

  const handlePasswordCreate = () => {
    if (token && isFormValid) {
      dispatch(createPasswordRequest({ password, confirmPassword, token }));
    }
  };

  return (
    <Grid container direction="column" alignItems="center">
      <Typography fontSize={20} color="#2A3C67" fontWeight="bold">
        {t('auth.createAccount')}
      </Typography>
      <Typography
        pt={2}
        fontSize={15}
        fontWeight={500}
        width={{ xs: '100%', md: '75%' }}
        textAlign="center"
        color="#2A3C67"
        sx={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {t('auth.welcome', { coachName: userInfo?.principalCoach })}
      </Typography>
      <Box textAlign="center" pt={2} maxWidth="100%">
        <Typography
          variant="body1"
          color="#2A3C67"
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {userInfo?.firstName}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {userInfo?.email}
        </Typography>
      </Box>
      <Grid item sx={{ width: '100%', marginTop: '16px' }}>
        <LabeledInput
          id="password"
          label="Password"
          placeholder="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          handleChange={(e) => setPassword(e.target.value)}
          startIcon={<img src="/assets/icons/security.svg" alt="security" />}
          endIcon={
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              <img
                src={
                  showPassword ? '/assets/icons/visibility.svg' : '/assets/icons/visibility-off.svg'
                }
                alt="toggle visibility"
              />
            </IconButton>
          }
        />
      </Grid>
      <Grid item sx={{ width: '100%', marginTop: '16px' }}>
        <LabeledInput
          id="confirmPassword"
          label="Confirm Password"
          placeholder="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          handleChange={(e) => setConfirmPassword(e.target.value)}
          startIcon={<img src="/assets/icons/security.svg" alt="security" />}
          endIcon={
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
            >
              <img
                src={
                  showConfirmPassword
                    ? '/assets/icons/visibility.svg'
                    : '/assets/icons/visibility-off.svg'
                }
                alt="toggle visibility"
              />
            </IconButton>
          }
        />
      </Grid>
      <Grid item sx={{ width: '100%', margin: '16px 0' }}>
        <Typography variant="bodyBold" color="primary">
          {t('changePassword.passwordValidations.PasswordMustContain')}
        </Typography>
        <Grid container spacing={1} pt={1.5} pb={4}>
          {passwordRequirements(password, confirmPassword)
            .slice(0, 5)
            .map((requirement) => (
              <Grid item container xs={12} md={6} key={requirement.text}>
                <img
                  src={`/assets/icons/${
                    requirement.isValid() ? 'check-circle-new' : 'close-circle'
                  }.svg`}
                  alt="check-circle-new"
                />
                <Typography variant="bodyRegular" color="primary" pl={1}>
                  {requirement.text}
                </Typography>
              </Grid>
            ))}
        </Grid>
        <StyledButton
          fullWidth
          disabled={!isFormValid || createPasswordLoading}
          onClick={handlePasswordCreate}
          loading={createPasswordLoading}
        >
          {t('therapistOnboarding.next')}
        </StyledButton>
      </Grid>
    </Grid>
  );
};

export default CreateAccountForm;
